import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useAppSelector } from "@/app/store";
import {
  selectUser,
  selectUserPermissions,
} from "@/app/store/slices/auth.slice";
import type { PermissionName } from "@/shared/constants";
import { evaluatePermissionCheck } from "@/shared/constants";

interface PermissionRouteGuardProps {
  children: React.ReactNode;
  permission?: PermissionName;
  anyOf?: PermissionName[];
  allOf?: PermissionName[];
  fallbackPath?: string;
}

/**
 * Protects routes based on user permissions.
 * Redirects to fallback path when the permission check fails.
 */
export function PermissionRouteGuard({
  children,
  permission,
  anyOf,
  allOf,
  fallbackPath = "/unauthorized",
}: PermissionRouteGuardProps) {
  const user = useAppSelector(selectUser);
  const permissions = useAppSelector(selectUserPermissions);

  const isAllowed = useMemo(() => {
    if (permission) {
      return evaluatePermissionCheck(permissions, { permission });
    }
    if (anyOf?.length) {
      return evaluatePermissionCheck(permissions, { anyOf });
    }
    if (allOf?.length) {
      return evaluatePermissionCheck(permissions, { allOf });
    }
    return true;
  }, [permissions, permission, anyOf, allOf]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAllowed) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}

export function withPermissionRouteGuard(
  Component: React.ComponentType,
  options: Omit<PermissionRouteGuardProps, "children">,
) {
  return function GuardedComponent() {
    return (
      <PermissionRouteGuard {...options}>
        <Component />
      </PermissionRouteGuard>
    );
  };
}
